(() => {
  const controllers = [];

  const escapeHtml = (value) => String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

  class VariantMetafieldGalleryController {
    constructor(productInfo, gallery) {
      this.productInfo = productInfo;
      this.gallery = gallery;
      this.mediaWrapper = gallery.querySelector(".m-media-gallery__list");
      this.thumbWrapper = gallery.querySelector(".m-product-media--slider__thumbnails .swiper-wrapper");
      this.dataEl = gallery.querySelector("[data-variant-metafield-gallery]");
      this.originalMediaHtml = this.mediaWrapper ? this.mediaWrapper.innerHTML : "";
      this.originalThumbHtml = this.thumbWrapper ? this.thumbWrapper.innerHTML : "";
      this.originalMediaCount = this.mediaWrapper ? this.mediaWrapper.querySelectorAll('.m-product-media--item').length : 0;
      this.data = this.parseData();
      this.activeVariantId = null;
      this.renderByVariantId(this.getCurrentVariantId());
    }

    parseData() {
      if (!this.dataEl) return {};
      try {
        return JSON.parse(this.dataEl.textContent || "{}");
      } catch (error) {
        console.warn("Variant metafield gallery JSON error:", error);
        return {};
      }
    }

    getCurrentVariantId() {
      const input = this.productInfo.querySelector('form[action*="/cart/add"] [name="id"], input[name="id"]');
      return input ? String(input.value) : null;
    }

    renderByVariantId(variantId) {
      if (!variantId || !this.mediaWrapper) return;
      const images = this.data[String(variantId)] || [];

      if (!images.length) {
        this.restoreOriginalGallery();
        this.activeVariantId = null;
        return;
      }

      if (this.activeVariantId === String(variantId)) return;
      this.activeVariantId = String(variantId);
      this.destroySliders();
      this.applyGalleryState(images.length);
      this.mediaWrapper.innerHTML = images.map((image, index) => this.renderMainSlide(image, index, variantId)).join("");
      if (this.thumbWrapper) {
        this.thumbWrapper.innerHTML = images.map((image, index) => this.renderThumbSlide(image, index, variantId)).join("");
      }
      this.refreshGallery();
    }

    applyGalleryState(count) {
      const mediaCount = Number(count) || 0;
      const hasMultiple = mediaCount > 1;

      this.gallery.dataset.mediaSize = String(mediaCount);
      this.gallery.dataset.onlyMedia = hasMultiple ? "false" : "true";
      this.gallery.dataset.enableDesktopSlider = hasMultiple ? "true" : "false";

      this.gallery.onlyMedia = !hasMultiple;
      this.gallery.enableDesktopSlider = hasMultiple;
    }

    restoreOriginalGallery() {
      if (!this.mediaWrapper) return;
      this.destroySliders();
      this.applyGalleryState(this.originalMediaCount);
      this.mediaWrapper.innerHTML = this.originalMediaHtml;
      if (this.thumbWrapper) this.thumbWrapper.innerHTML = this.originalThumbHtml;
      this.refreshGallery();
    }

    renderMainSlide(image, index, variantId) {
      const src = image.src || image.zoom || image.thumb || "";
      const zoom = image.zoom || src;
      const alt = escapeHtml(image.alt || "");
      const width = Number(image.width) || 1600;
      const height = Number(image.height) || 1600;
      const aspectRatio = Number(image.aspectRatio) || (width && height ? width / height : 1);
      const mediaId = `variant-metafield-${variantId}-${index}`;
      const spanClass = this.gallery.dataset.layout === "layout-2" && index % 3 === 0 ? " m-col-span-2" : "";

      return `
        <div class="m-product-media--item media-type-image swiper-slide m:column${spanClass}" data-index="${index}" data-media-type="image" data-media-id="${mediaId}" data-aspect-ratio="${aspectRatio}">
          <div class="m-product-media" data-media-src="${escapeHtml(zoom)}" data-media-width="${width}" data-media-height="${height}" data-media-alt="${alt}" style="--animation-order: ${index + 1};">
            <img src="${escapeHtml(src)}" alt="${alt}" width="${width}" height="${height}" loading="${index === 0 ? "eager" : "lazy"}" style="width:100%;height:auto;display:block;">
          </div>
        </div>`;
    }

    renderThumbSlide(image, index, variantId) {
      const thumb = image.thumb || image.src || image.zoom || "";
      const alt = escapeHtml(image.alt || "");
      const width = Number(image.width) || 1600;
      const height = Number(image.height) || 1600;
      const aspectRatio = Number(image.aspectRatio) || (width && height ? width / height : 1);
      const mediaId = `variant-metafield-${variantId}-${index}`;
      const slideHeight = Math.max(1, Math.round(70 / aspectRatio));
      const activeClass = index === 0 ? " swiper-slide-thumb-active" : "";

      return `
        <div class="swiper-slide media-type-image${activeClass}" data-index="${index}" data-media-type="image" data-media-id="${mediaId}" data-aspect-ratio="${aspectRatio}" style="--slide-height: ${slideHeight}px;">
          <div class="m-product-media">
            <img src="${escapeHtml(thumb)}" alt="${alt}" width="240" height="240" loading="lazy" style="width:100%;height:100%;object-fit:cover;display:block;">
          </div>
        </div>`;
    }

    destroySliders() {
      if (this.gallery.slider && typeof this.gallery.slider.destroy === "function") {
        this.gallery.slider.destroy(true, true);
        this.gallery.slider = false;
      }
      if (this.gallery.navSlider && typeof this.gallery.navSlider.destroy === "function") {
        this.gallery.navSlider.destroy(true, true);
        this.gallery.navSlider = false;
      }
    }

    refreshGallery() {
      if (typeof queryDomNodes === "function" && this.gallery.selectors) {
        this.gallery.domNodes = queryDomNodes(this.gallery.selectors, this.gallery);
      }

      if (typeof this.gallery.setSliderOptions === "function") this.gallery.setSliderOptions();
      if (typeof this.gallery.updateMediaLayout === "function") this.gallery.updateMediaLayout();

      if (this.gallery.slider && typeof this.gallery.slider.slideTo === "function") {
        this.gallery.slider.update?.();
        this.gallery.slider.slideTo(0, 0, false);
      }
      if (this.gallery.navSlider && typeof this.gallery.navSlider.slideTo === "function") {
        this.gallery.navSlider.update?.();
        this.gallery.navSlider.slideTo(0, 0, false);
      }

      if (this.gallery.lightbox && typeof this.gallery.lightbox.destroy === "function") {
        this.gallery.lightbox.destroy();
        this.gallery.lightbox = null;
      }
      if (this.gallery.enableImageZoom && typeof this.gallery.handlePhotoswipe === "function") {
        this.gallery.handlePhotoswipe();
      }
    }
  }

  const initControllers = () => {
    document.querySelectorAll("product-info").forEach((productInfo) => {
      if (controllers.some((controller) => controller.productInfo === productInfo)) return;
      const gallery = productInfo.querySelector('media-gallery[data-enable-variant-metafield-gallery="true"]');
      if (!gallery) return;
      controllers.push(new VariantMetafieldGalleryController(productInfo, gallery));
    });
  };

  document.addEventListener("DOMContentLoaded", initControllers);
  window.addEventListener("load", initControllers);

  document.addEventListener("variant:changed", (event) => {
    initControllers();
    const variantId = event.detail && event.detail.variant ? String(event.detail.variant.id) : null;
    controllers.forEach((controller) => {
      const nextVariantId = variantId || controller.getCurrentVariantId();
      controller.renderByVariantId(nextVariantId);
    });
  });
})();